/**
 * Der Stichtag eines Rückblicks — für die ganze Anwendung.
 *
 * ## Warum ein Rahmen und keine Eigenschaft
 *
 * Der Stichtag betrifft **jeden** Abruf. Als Eigenschaft durchgereicht wären
 * das zweiundfünfzig Signaturen und vierzig Routen; als Rahmen ist es eine
 * Stelle. Dasselbe Muster wie `Modulrahmen.tsx`: er liegt über der Anwendung,
 * wer ihn braucht, liest ihn.
 *
 * ## Was hier entschieden wird
 *
 * Der Mensch wählt einen **Tag**. Die Schnittstelle bekommt einen
 * **Zeitpunkt** — das Ende dieses Tages, in der Zeitzone des Browsers
 * gerechnet und als UTC gesendet. Das ist keine Feinheit: „der Stand vom
 * 9. September" endet in Berlin zwei Stunden früher als in UTC, und eine
 * Änderung in diesen zwei Stunden fiele sonst auf den falschen Tag.
 *
 * ## Wo er sonst noch steht
 *
 * `api/klient.ts` führt denselben Wert in einer Modulvariablen und setzt ihn
 * als Kopf `X-Stand` an jeden Abruf. Zwei Orte für einen Wert wären zwei
 * Wahrheiten, deshalb schreibt **nur** dieser Rahmen dorthin — über
 * `standSetzen`, bei jeder Änderung.
 *
 * Er überdauert ein Neuladen (`sessionStorage`), aber keine neue Sitzung:
 * wer den Browser schließt, sieht beim nächsten Mal den heutigen Stand. Ein
 * Rückblick, den man vergisst und für die Gegenwart hält, wäre das
 * Gefährlichste an dieser Funktion.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import { standSetzen } from '../api/klient';

/** Wie weit zurück — dieselbe Frist wie die Aufbewahrung des Verlaufs. */
export const STICHTAG_TAGE = 30;

const SCHLUESSEL = 'iammatrix.stand';

export interface Stand {
  /** Der gewählte Tag, `JJJJ-MM-TT` — das, was der Mensch gewählt hat. */
  tag: string;
  /** Das Ende dieses Tages als Zeitpunkt in UTC — das, was die API bekommt. */
  zeitpunkt: string;
}

interface Api {
  stand: Stand | null;
  /** Einen Tag wählen, `null` heißt zurück zu heute. */
  setzen: (tag: string | null) => void;
}

const Zusammenhang = createContext<Api | null>(null);

/** `JJJJ-MM-TT` eines Tages, der `abstand` Tage zurückliegt. */
export function tagVor(abstand: number): string {
  const d = new Date();
  d.setDate(d.getDate() - abstand);
  const zwei = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${zwei(d.getMonth() + 1)}-${zwei(d.getDate())}`;
}

/**
 * Das Ende eines Tages als UTC-Zeitpunkt.
 *
 * Gerechnet wird über Mitternacht des **Folgetags** in der Zeitzone des
 * Browsers, minus eine Millisekunde — so trifft es die Sommerzeit von selbst,
 * ohne eine Tabelle von Zeitzonen.
 *
 * Gedeckelt auf „jetzt": ein Zeitpunkt in der Zukunft hat keinen Stand, und
 * die Schnittstelle weist ihn ab.
 */
export function tagesende(tag: string): string {
  const [jahr, monat, tagZahl] = tag.split('-').map(Number);
  const ende = new Date(jahr ?? 1970, (monat ?? 1) - 1, (tagZahl ?? 1) + 1).getTime() - 1;
  return new Date(Math.min(ende, Date.now() - 1000)).toISOString();
}

function ausSpeicher(): Stand | null {
  try {
    const roh = sessionStorage.getItem(SCHLUESSEL);
    if (!roh) return null;
    const tag = String(JSON.parse(roh).tag ?? '');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(tag)) return null;
    // Abgelaufen? Dann ist es kein Stand mehr, sondern ein alter Zettel.
    if (tag < tagVor(STICHTAG_TAGE)) return null;
    return { tag, zeitpunkt: tagesende(tag) };
  } catch {
    return null;
  }
}

export function Standrahmen({ children }: { children: ReactNode }) {
  const [stand, setzeStand] = useState<Stand | null>(ausSpeicher);

  /* Der Klient erfährt es sofort und nicht erst beim nächsten Zeichnen:
     zwischen „gesetzt" und „gezeichnet" liegt sonst ein Abruf, der den Kopf
     noch nicht trägt — und der brächte den heutigen Stand unter das Band. */
  useEffect(() => {
    standSetzen(stand?.zeitpunkt ?? null);
  }, [stand]);

  const setzen = useCallback((tag: string | null) => {
    const neu = tag ? { tag, zeitpunkt: tagesende(tag) } : null;
    standSetzen(neu?.zeitpunkt ?? null);
    setzeStand(neu);
    try {
      if (neu) sessionStorage.setItem(SCHLUESSEL, JSON.stringify({ tag: neu.tag }));
      else sessionStorage.removeItem(SCHLUESSEL);
    } catch { /* kein Speicher verfuegbar — der Stand gilt dann nur bis zum Neuladen */ }
  }, []);

  const wert = useMemo(() => ({ stand, setzen }), [stand, setzen]);
  return <Zusammenhang.Provider value={wert}>{children}</Zusammenhang.Provider>;
}

/** Der gewählte Stichtag, oder `null` für heute. */
export function useStand(): Stand | null {
  return useContext(Zusammenhang)?.stand ?? null;
}

/** Den Stichtag setzen — der Wähler in der Kopfzeile und das Band benutzen es. */
export function useStandSetzen(): (tag: string | null) => void {
  const rahmen = useContext(Zusammenhang);
  return rahmen?.setzen ?? (() => { /* ohne Rahmen gibt es nichts zu setzen */ });
}
